import React from 'react';
import { Text, makeStyles, tokens } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import type { SpaceDetails } from '@store/models/booking';
import type { Building } from '@store/models/buildings/entities';
import { formatUtcDateInTimeZone, formatUtcTimeInTimeZone } from '@/utils/timeZone';
import { formatTime12Hour } from '@utils/dateFormat';

export interface ThankYouCondensedSummaryProps {
  formData: Record<string, unknown>;
  selectedSpace: SpaceDetails | null;
  selectedBuilding?: Building | null;
  /** Submitted request id (REF-XXXX) — rendered as the first row when present. */
  requestId?: string | null;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    width: '100%',
    marginTop: tokens.spacingVerticalL,
    paddingTop: tokens.spacingVerticalL,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    textAlign: 'left',
  },
  heading: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginBottom: tokens.spacingVerticalXS,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: 'minmax(8rem, 12rem) 1fr',
    columnGap: tokens.spacingHorizontalM,
    rowGap: tokens.spacingVerticalXS,
    alignItems: 'baseline',
  },
  label: {
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
  },
  value: {
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
    wordBreak: 'break-word',
  },
});

const formatDateValue = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

interface DateRangeValue {
  start: string | null;
  end: string | null;
}
interface TimeRangeValue {
  start: string;
  end: string;
}

export const ThankYouCondensedSummary: React.FC<ThankYouCondensedSummaryProps> = ({
  formData,
  selectedSpace,
  selectedBuilding,
  requestId,
}) => {
  const { t } = useTranslation('bookingForm');
  const styles = useStyles();

  const eventTitle = (formData.eventTitle as string | undefined) ?? '';
  const dateRange = (formData.eventDateRange as DateRangeValue | undefined) ?? {
    start: null,
    end: null,
  };
  const timeRange = (formData.eventTimeRange as TimeRangeValue | undefined) ?? {
    start: '',
    end: '',
  };
  const attendees = formData.numberOfAttendees as number | undefined;

  const utcStart = formData.eventStartDate as string | undefined;
  const utcEnd = formData.eventEndDate as string | undefined;
  const timeZone = selectedBuilding?.gmtZone;
  const timeZoneShort = selectedBuilding?.GMTZoneShort;

  const timeOpts: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };

  // Prefer UTC-to-building-tz conversion; fall back to raw form values.
  const startDateStr = utcStart
    ? formatUtcDateInTimeZone(utcStart, timeZone)
    : formatDateValue(dateRange.start);
  const endDateStr = utcEnd
    ? formatUtcDateInTimeZone(utcEnd, timeZone)
    : formatDateValue(dateRange.end);
  const startTimeStr = utcStart
    ? formatUtcTimeInTimeZone(utcStart, 'en-US', timeZone, timeOpts)
    : timeRange.start
      ? formatTime12Hour(timeRange.start)
      : '';
  const endTimeStr = utcEnd
    ? formatUtcTimeInTimeZone(utcEnd, 'en-US', timeZone, timeOpts)
    : timeRange.end
      ? formatTime12Hour(timeRange.end)
      : '';
  const tzSuffix = timeZoneShort ? ` ${timeZoneShort}` : '';

  const spaceLabel = selectedSpace?.name ?? '';
  const buildingLabel = selectedBuilding?.buildingName ?? selectedBuilding?.id ?? '';
  const spaceWithBuilding =
    spaceLabel && buildingLabel ? `${spaceLabel} — ${buildingLabel}` : spaceLabel || buildingLabel;

  return (
    <div className={styles.root} data-testid="thank-you-condensed-summary">
      <Text as="h2" className={styles.heading}>
        {t('bookingForm.sections.eventInfo')}
      </Text>
      <div className={styles.row}>
        {requestId && (
          <>
            <Text className={styles.label}>{t('bookingForm.review.requestId')}</Text>
            <Text className={styles.value} data-testid="summary-request-id">
              {requestId}
            </Text>
          </>
        )}
        <Text className={styles.label}>{t('bookingForm.review.eventTitle')}</Text>
        <Text className={styles.value} data-testid="summary-event-title">
          {eventTitle}
        </Text>

        <Text className={styles.label}>{t('bookingForm.fields.startDate.label')}</Text>
        <Text className={styles.value} data-testid="summary-start-datetime">
          {startDateStr}
          {startTimeStr ? ` ${startTimeStr}${tzSuffix}` : ''}
        </Text>

        <Text className={styles.label}>{t('bookingForm.fields.endDate.label')}</Text>
        <Text className={styles.value} data-testid="summary-end-datetime">
          {endDateStr}
          {endTimeStr ? ` ${endTimeStr}${tzSuffix}` : ''}
        </Text>

        {spaceWithBuilding && (
          <>
            <Text className={styles.label}>{t('bookingForm.review.selectedSpace')}</Text>
            <Text className={styles.value} data-testid="summary-space">
              {spaceWithBuilding}
            </Text>
          </>
        )}

        {typeof attendees === 'number' && (
          <>
            <Text className={styles.label}>{t('bookingForm.review.attendees')}</Text>
            <Text className={styles.value} data-testid="summary-attendees">
              {attendees}
            </Text>
          </>
        )}
      </div>
    </div>
  );
};

ThankYouCondensedSummary.displayName = 'ThankYouCondensedSummary';
